import { createApiWarmUp, noApiWarmUp, type WarmUpApi } from './api-warm-up';
import type { FirebaseSession } from './firebase-session';
import { hasPersistedUser } from './persisted-user';
import { createSessionClient, type EndSession, type ExchangeSession } from './session-client';

export type { SignedInUser } from './firebase-session';

/**
 * Everything the identity screens need from Firebase and the API. Injectable so pages can be tested and shown. The
 * Firebase calls load the SDK on first use.
 */
export interface IdentitySession extends FirebaseSession {
  /** Whether a Firebase user may be signed in on this device, without loading the SDK. */
  mayBeSignedIn: () => boolean;
  /** POST /session. */
  exchange: ExchangeSession;
  /** POST /session/sign-out. */
  endSession: EndSession;
  /** GET /health-check, so an API that has scaled to zero starts while the player types: once per page load. */
  warmUpApi: WarmUpApi;
}

/** The Firebase side, loaded only when it is needed (see load-email-auth.ts). */
const firebase = async () => (await import('../auth/browser-email-auth')).browserFirebaseSession;

const client = createSessionClient(API_ORIGIN, (...args) => fetch(...args));

// Module scope, so it sends once per page load whichever page calls it, and however often.
const warmUpApi = WARM_UP_API ? createApiWarmUp(API_ORIGIN, (...args) => fetch(...args)) : noApiWarmUp;

export const browserIdentitySession: IdentitySession = {
  mayBeSignedIn: () => hasPersistedUser(import.meta.env.VITE_FIREBASE_API_KEY),
  signedInUser: async () => (await firebase()).signedInUser(),
  refreshUser: async () => (await firebase()).refreshUser(),
  freshIdToken: async () => (await firebase()).freshIdToken(),
  resendVerification: async (continueUrl) => (await firebase()).resendVerification(continueUrl),
  applyVerificationCode: async (oobCode) => (await firebase()).applyVerificationCode(oobCode),
  signOut: async () => (await firebase()).signOut(),
  exchange: client.exchange,
  endSession: client.endSession,
  warmUpApi,
};
